/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { FullProduct } from '@msdyn365-commerce/commerce-entities';
import {
    BaseNotificationComponent,
    ClosableNotificationWrapper,
    DismissibleNotificationWrapper,
    INotificationCloseButtonProps,
    INotificationProps,
    NotificationCloseButton,
    NotificationContent,
    NotificationDescription,
    NotificationFooter,
    NotificationHeader,
    NotificationInstance,
    NotificationTitle
} from '@msdyn365-commerce-modules/notifications-core';
import { Button, getPayloadObject, getTelemetryAttributes, TelemetryConstant } from '@msdyn365-commerce-modules/utilities';
import React from 'react';

import { OrderTemplateProductsPreviewComponent } from '../../order-template/order-templates-list/order-template-products-preview';
import { ItemsAddedErrorComponent } from '../items-added-to-cart-error-component';
import { MultiItemsSuccessfullyAddedToCartNotification } from './multi-items-successfully-added-to-cart-notification';

/**
 * Interface of props for multiple items are successfully added to cart notification.
 */
export interface IMultiItemsSuccessfullyAddedToCartNotificationProps
    extends INotificationProps<MultiItemsSuccessfullyAddedToCartNotification>,
        INotificationCloseButtonProps<MultiItemsSuccessfullyAddedToCartNotification> {}

/**
 * Multiple items are successfully added to cart notification.
 */
export class MultiItemsSuccessfullyAddedToCartNotificationComponent extends BaseNotificationComponent<
    MultiItemsSuccessfullyAddedToCartNotification,
    IMultiItemsSuccessfullyAddedToCartNotificationProps
> {
    public static readonly className: string = 'msc-notification-items-added-to-cart';

    public constructor(props: IMultiItemsSuccessfullyAddedToCartNotificationProps) {
        super(props);
    }

    public render(): React.ReactNode {
        const payLoad = getPayloadObject('click', this.props.notification.telemetryContent, TelemetryConstant.ContinueShopping);
        payLoad.contentAction.etext = TelemetryConstant.ViewBagAndCheckout;
        const viewBagAttribute = getTelemetryAttributes(this.props.notification.telemetryContent, payLoad);
        if (this.props.notification.imageSettings) {
            this.props.notification.imageSettings.cropFocalRegion = true;
        }

        const fullProducts: FullProduct[] = this.props.notification.products.map(item => {
            return { ProductDetails: item.product };
        });

        const className = MultiItemsSuccessfullyAddedToCartNotificationComponent.className;
        const footerClassName = NotificationFooter.className;
        return (
            <div role='alert' className={className}>
                <ClosableNotificationWrapper notification={this.props.notification}>
                    <DismissibleNotificationWrapper notification={this.props.notification}>
                        <NotificationInstance notification={this.props.notification}>
                            <NotificationHeader notification={this.props.notification}>
                                <NotificationTitle notification={this.props.notification} />
                                <NotificationCloseButton notification={this.props.notification} />
                            </NotificationHeader>
                            <NotificationContent notification={this.props.notification}>
                                <OrderTemplateProductsPreviewComponent
                                    context={this.props.notification.context}
                                    className='msc-notification-content__preview'
                                    products={fullProducts}
                                    imageSettings={this.props.notification.imageSettings}
                                />
                                <NotificationDescription notification={this.props.notification} />
                                <ItemsAddedErrorComponent
                                    errorMessage={this.props.notification.resources.itemsAddedValidationErrorMessage}
                                />
                            </NotificationContent>
                            <NotificationFooter notification={this.props.notification}>
                                {this.props.notification.navigationUrl && (
                                    <Button
                                        className={`${footerClassName}__go-to-cart`}
                                        href={this.props.notification.navigationUrl}
                                        title={this.props.notification.resources.viewCartButtonText}
                                        {...viewBagAttribute}
                                    >
                                        {this.props.notification.resources.viewCartButtonText}
                                    </Button>
                                )}
                            </NotificationFooter>
                        </NotificationInstance>
                    </DismissibleNotificationWrapper>
                </ClosableNotificationWrapper>
            </div>
        );
    }
}
